/**
 * Volunteer self-signup: the WRITE wire contract (docs/VOLUNTEER_SIGNUP.md).
 *
 * Staff bodies (`/api/v1/volunteer-signup/...`, Pattern B / B+) and the public
 * bodies (`/api/v1/public/volunteer-signup/:token/...`). Every request object
 * is `.strict()` (zod otherwise strips unknown keys silently). Text is
 * scrubbed with the survey scrubber and stored as PLAIN TEXT (React text nodes
 * only). The DB CHECKs are the backstop for every cap here.
 *
 * Shift times are VENUE wall-clock stamps (`localStampSchema`). A shift is at
 * most 24 h: equal start and end entered in the grid mean a full day
 * (`toShiftStamps`), so the stored pair is `ends_at > starts_at` and
 * `ends_at - starts_at <= 1440` minutes.
 */
import { z } from "zod";
import { calendarDateSchema, localStampSchema, localTimeSchema } from "./dateWindow.js";
import { MINUTES_PER_DAY, diffLocalStampMinutes } from "../format/date.js";
import { pdfPresentationSchema } from "./pdf.js";
import { SURVEY_EMAIL_MAX, SURVEY_HONEYPOT_FIELD, SURVEY_LONG_TEXT_MAX, SURVEY_MAX_QUESTIONS, SURVEY_PHONE_MAX, isAnswerableQuestion, optionalSurveyText, surveyQuestionSchema, surveyText, } from "./surveys.js";
import { VOLUNTEER_LOCALES, VOLUNTEER_SHIFT_VISIBILITIES, VOLUNTEER_VERIFICATION_MODES, } from "./volunteerSignupReads.js";
// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------
/** The page's public token (`/v/:token`): 22 base62 chars, ~131 bits. */
export const VOLUNTEER_PUBLIC_TOKEN_RE = /^[A-Za-z0-9]{22}$/;
export const VOLUNTEER_PUBLIC_TOKEN_LENGTH = 22;
/** A raw access link (`#<link>`): 32 random bytes as hex. Only its sha256 hex is stored. */
export const VOLUNTEER_LINK_RE = /^[0-9a-f]{64}$/;
export function isVolunteerPublicToken(value) {
    return typeof value === "string" && VOLUNTEER_PUBLIC_TOKEN_RE.test(value);
}
export function isVolunteerLink(value) {
    return typeof value === "string" && VOLUNTEER_LINK_RE.test(value);
}
// ---------------------------------------------------------------------------
// Limits (mirrored by the migration's CHECKs)
// ---------------------------------------------------------------------------
export const VOLUNTEER_ROLE_NAME_MAX = 120;
export const VOLUNTEER_ROLE_DESCRIPTION_MAX = 2_000;
export const VOLUNTEER_ROLE_LEADS_MAX = 50;
export const VOLUNTEER_SHIFT_CAPACITY_MAX = 1_000;
export const VOLUNTEER_SHIFT_NAME_MAX = 120;
export const VOLUNTEER_SHIFT_DESCRIPTION_MAX = 1_000;
export const VOLUNTEER_SHIFTS_BULK_MAX = 200;
export const VOLUNTEER_PAGE_TITLE_MAX = 200;
export const VOLUNTEER_WELCOME_MAX = 5_000;
/** The page's "Getting there" Markdown (20261019180000 CHECK): the welcome message's cap. */
export const VOLUNTEER_GETTING_THERE_MAX = 5_000;
/** A volunteer's note when they give up a shift (20261019180000 CHECK). */
export const VOLUNTEER_CANCEL_NOTE_MAX = 500;
/** A role's welcome email (20261010110000 CHECKs): subject one line <= 200, Markdown body <= 10000. */
export const VOLUNTEER_ROLE_WELCOME_SUBJECT_MAX = 200;
export const VOLUNTEER_ROLE_WELCOME_BODY_MAX = 10_000;
export const VOLUNTEER_IMAGE_ALT_MAX = 300;
export const VOLUNTEER_CANCEL_CUTOFF_HOURS_MAX = 720;
export const VOLUNTEER_RETENTION_DAYS_MAX = 365;
/** 1000 h — a generous upper bound for an hours threshold. */
export const VOLUNTEER_HOURS_THRESHOLD_MINUTES_MAX = 60_000;
export const VOLUNTEER_INTAKE_QUESTIONS_MAX = SURVEY_MAX_QUESTIONS;
export const VOLUNTEER_CONSENT_TITLE_MAX = 200;
export const VOLUNTEER_CONSENT_BODY_MAX = 20_000;
export const VOLUNTEER_TYPED_NAME_MAX = 200;
export const VOLUNTEER_NAME_MAX = 200;
export const VOLUNTEER_CLAIM_SHIFTS_MAX = 50;
/** The events one signup page may list explicitly (`volunteer_page_events`). */
export const VOLUNTEER_PAGE_EVENTS_MAX = 500;
const uuid = z.string().uuid();
const uniqueIds = (ids) => new Set(ids).size === ids.length;
const emailSchema = z.string().trim().toLowerCase().email().max(SURVEY_EMAIL_MAX);
const localeSchema = z.enum(VOLUNTEER_LOCALES);
const nameSchema = surveyText(VOLUNTEER_NAME_MAX, { min: 1 });
const phoneSchema = optionalSurveyText(SURVEY_PHONE_MAX);
// ---------------------------------------------------------------------------
// Roles + leads
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Crew WhatsApp group (one invite link per role; docs/VOLUNTEER_SIGNUP.md ruling 38)
// ---------------------------------------------------------------------------
/**
 * The stored shape of `volunteer_roles.whatsapp_url`, the SAME pattern as the
 * DB CHECK `volunteer_roles_whatsapp_url_check` (20261020150000; a test pins
 * the two together): https, host `chat.whatsapp.com`, one 16..64 character
 * alphanumeric invite code, no query and no hash.
 */
export const WHATSAPP_INVITE_URL_RE = /^https:\/\/chat\.whatsapp\.com\/[A-Za-z0-9]{16,64}$/;
/** The issue message when an invite link does not normalise (the web maps it to an i18n key). */
export const VOLUNTEER_INVALID_WHATSAPP_URL = "invalid_whatsapp_url";
/**
 * The ONE invite link normaliser: what an organiser pastes (WhatsApp's "Copy
 * link", maybe with `?mode=…` or a trailing slash, maybe without a scheme) →
 * the stored `https://chat.whatsapp.com/<code>`, or `null` when it is not a
 * WhatsApp group invite. Trims; takes https only (a bare `chat.whatsapp.com/…`
 * gets `https://`); the host must be exactly `chat.whatsapp.com` (no
 * credentials, no port, no lookalike); the path one code segment; the query
 * and hash are dropped.
 */
export function normalizeWhatsappInviteUrl(raw) {
    const value = (raw ?? "").trim();
    if (!value)
        return null;
    const withScheme = /^chat\.whatsapp\.com\//i.test(value) ? `https://${value}` : value;
    let url;
    try {
        url = new URL(withScheme);
    }
    catch {
        return null;
    }
    if (url.protocol !== "https:" || url.username || url.password || url.port)
        return null;
    if (url.hostname.toLowerCase() !== "chat.whatsapp.com")
        return null;
    const segments = url.pathname.split("/").filter(Boolean);
    if (segments.length !== 1)
        return null;
    const normalized = `https://chat.whatsapp.com/${segments[0]}`;
    return WHATSAPP_INVITE_URL_RE.test(normalized) ? normalized : null;
}
/**
 * A role's crew WhatsApp link in a write body: presence-keyed (absent = keep),
 * `null` or blank = remove, otherwise it must pass `normalizeWhatsappInviteUrl`
 * and is STORED normalised; anything else is a 400 carrying
 * `VOLUNTEER_INVALID_WHATSAPP_URL`. The DB CHECK is the backstop.
 */
export const whatsappInviteUrlSchema = z
    .string()
    .max(2_048)
    .transform((value, ctx) => {
    if (!value.trim())
        return null;
    const normalized = normalizeWhatsappInviteUrl(value);
    if (!normalized) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: VOLUNTEER_INVALID_WHATSAPP_URL });
        return z.NEVER;
    }
    return normalized;
})
    .nullable()
    .optional();
const roleFields = {
    name: surveyText(VOLUNTEER_ROLE_NAME_MAX, { min: 1 }),
    public_description: optionalSurveyText(VOLUNTEER_ROLE_DESCRIPTION_MAX, true),
    /** The welcome email (presence-keyed like public_description; blank → null). Single line. */
    welcome_subject: optionalSurveyText(VOLUNTEER_ROLE_WELCOME_SUBJECT_MAX),
    /** The welcome email's Markdown (the escape-first subset, `renderEmailMarkdown`); blank → null = no welcome. */
    welcome_body: optionalSurveyText(VOLUNTEER_ROLE_WELCOME_BODY_MAX, true),
    /** The crew WhatsApp group invite link (presence-keyed; blank or null → removed). Set from the phone app. */
    whatsapp_url: whatsappInviteUrlSchema,
    self_signup_enabled: z.boolean(),
    requires_approval: z.boolean(),
    sort_order: z.number().int().min(0).max(10_000),
};
/** POST …/roles. */
export const volunteerRoleWriteSchema = z
    .object({
    ...roleFields,
    self_signup_enabled: roleFields.self_signup_enabled.optional(),
    requires_approval: roleFields.requires_approval.optional(),
    sort_order: roleFields.sort_order.optional(),
})
    .strict();
/** PATCH …/roles/:id — presence-keyed, at least one key. */
export const volunteerRolePatchSchema = z
    .object({
    name: roleFields.name.optional(),
    public_description: roleFields.public_description,
    welcome_subject: roleFields.welcome_subject,
    welcome_body: roleFields.welcome_body,
    whatsapp_url: roleFields.whatsapp_url,
    self_signup_enabled: roleFields.self_signup_enabled.optional(),
    requires_approval: roleFields.requires_approval.optional(),
    sort_order: roleFields.sort_order.optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: "At least one field must be provided" });
/**
 * One lead in a leads PUT. `phone` is presence-keyed: absent = keep the
 * number the user already has in this project (a new row inherits it), `null`
 * or blank = clear it, a value = set it. One number per (user, project): the
 * route writes it to every lead row of that user in the project.
 */
export const volunteerRoleLeadSchema = z.object({ user_id: uuid, phone: phoneSchema }).strict();
const leadsList = z
    .array(volunteerRoleLeadSchema)
    .max(VOLUNTEER_ROLE_LEADS_MAX)
    .refine((leads) => uniqueIds(leads.map((l) => l.user_id)), "leads must not repeat a user_id");
/**
 * PUT …/roles/:id/leads and …/events/:eventId/leads — the full set (org
 * members). The body is `{ leads: [{ user_id, phone? }] }`; the earlier
 * `{ user_ids }` is still accepted for one release (no phone keys: every
 * number is kept). Both parse to `{ leads }`.
 */
export const volunteerRoleLeadsSchema = z
    .union([
    z.object({ leads: leadsList }).strict(),
    z
        .object({
        user_ids: z.array(uuid).max(VOLUNTEER_ROLE_LEADS_MAX).refine(uniqueIds, "user_ids must not repeat"),
    })
        .strict(),
])
    .transform((body) => "leads" in body ? { leads: body.leads } : { leads: body.user_ids.map((user_id) => ({ user_id })) });
/**
 * POST …/roles/:roleId/seen — stamps the caller's "last seen" of the role
 * (`event_id` absent / null) or of the role at one planner event of the project.
 */
export const volunteerRoleSeenSchema = z.object({ event_id: uuid.nullable().optional() }).strict();
/**
 * PUT …/roles/:roleId/events/:eventId — the role's overrides at one planner
 * event (upsert). Presence-keyed like the role PATCH: an absent key is
 * untouched (on a first write: NULL), `null` = inherit the role's setting
 * again. At least one key. The event leads have their own PUT
 * (…/events/:eventId/leads, `volunteerRoleLeadsSchema`); the DELETE of the
 * pair resets the overrides only.
 */
export const volunteerRoleEventWriteSchema = z
    .object({
    public_description: roleFields.public_description,
    /**
     * The welcome override, taken AS A PAIR with welcome_body: when this
     * row's body is set the pair wins (a null subject = the default
     * subject); a null/blank body = inherit the role's pair.
     */
    welcome_subject: roleFields.welcome_subject,
    welcome_body: roleFields.welcome_body,
    self_signup_enabled: z.boolean().nullable().optional(),
    requires_approval: z.boolean().nullable().optional(),
    /** The default place offered for a new shift of this role at this event. */
    project_place_id: uuid.nullable().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: "At least one field must be provided" });
// ---------------------------------------------------------------------------
// Shifts
// ---------------------------------------------------------------------------
/** `ends_at > starts_at` and at most 24 h (the DB CHECKs). */
export function isValidShiftSpan(starts_at, ends_at) {
    const minutes = diffLocalStampMinutes(starts_at, ends_at);
    return minutes !== null && minutes > 0 && minutes <= MINUTES_PER_DAY;
}
const SHIFT_SPAN_MESSAGE = {
    message: "ends_at must be after starts_at and at most 24 hours later",
    path: ["ends_at"],
};
const shiftFields = {
    role_id: uuid,
    starts_at: localStampSchema,
    ends_at: localStampSchema,
    capacity: z.number().int().min(1).max(VOLUNTEER_SHIFT_CAPACITY_MAX),
    project_place_id: uuid.nullable().optional(),
    /** Optional link to one of the project's planner events. */
    project_event_id: uuid.nullable().optional(),
    /** Optional short name, one line; labels read "Role · Name" (`shiftHeading`). Blank → null. */
    name: optionalSurveyText(VOLUNTEER_SHIFT_NAME_MAX),
    description: optionalSurveyText(VOLUNTEER_SHIFT_DESCRIPTION_MAX, true),
    visibility: z.enum(VOLUNTEER_SHIFT_VISIBILITIES),
};
/** One shift to create. */
export const volunteerShiftInputSchema = z
    .object({ ...shiftFields, visibility: shiftFields.visibility.default("public") })
    .strict()
    .refine((v) => isValidShiftSpan(v.starts_at, v.ends_at), SHIFT_SPAN_MESSAGE);
/** POST …/shifts — 1..200 shifts in one request (the Add shift dialog sends one). */
export const volunteerShiftsBulkSchema = z
    .object({ shifts: z.array(volunteerShiftInputSchema).min(1).max(VOLUNTEER_SHIFTS_BULK_MAX) })
    .strict();
/**
 * PATCH …/shifts/:id — presence-keyed, at least one key. The span check runs
 * when both stamps are sent; the DB CHECK is the backstop for a one-sided move.
 */
export const volunteerShiftPatchSchema = z
    .object({
    role_id: shiftFields.role_id.optional(),
    starts_at: shiftFields.starts_at.optional(),
    ends_at: shiftFields.ends_at.optional(),
    capacity: shiftFields.capacity.optional(),
    project_place_id: shiftFields.project_place_id,
    project_event_id: shiftFields.project_event_id,
    name: shiftFields.name,
    description: shiftFields.description,
    visibility: shiftFields.visibility.optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: "At least one field must be provided" })
    .refine((v) => v.starts_at === undefined || v.ends_at === undefined || isValidShiftSpan(v.starts_at, v.ends_at), SHIFT_SPAN_MESSAGE);
// ---------------------------------------------------------------------------
// Intake questions (the survey question schema, own storage)
// ---------------------------------------------------------------------------
const isActive = (item) => item.retired !== true;
/**
 * A required question that no new answer could satisfy: a choice with no
 * active option, or a grid with no active row or column (everything retired).
 */
function requiredWithoutActiveChoice(q) {
    if (!isAnswerableQuestion(q) || q.required !== true || q.retired === true)
        return false;
    if (q.type === "single_choice" || q.type === "dropdown" || q.type === "multi_choice") {
        return !q.options.some(isActive);
    }
    if (q.type === "choice_grid" || q.type === "checkbox_grid") {
        return !q.rows.some(isActive) || !q.columns.some(isActive);
    }
    return false;
}
/**
 * The page's `intake_questions`: 0..50 survey questions with unique ids. A
 * `sensitive` question is never `required` (sensitive answers are optional
 * and need their own Art. 9 tick), and a required, non-retired choice / grid
 * question keeps at least one active option (row and column): retiring them
 * all would make it unanswerable for every new volunteer. The editor locks
 * the last one; this refuses the bypass (retire all while optional, then
 * flip to required).
 */
export const intakeQuestionsSchema = z
    .array(surveyQuestionSchema)
    .max(VOLUNTEER_INTAKE_QUESTIONS_MAX)
    .superRefine((questions, ctx) => {
    const seen = new Set();
    questions.forEach((q, index) => {
        if (seen.has(q.id)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "duplicate question id", path: [index, "id"] });
        }
        seen.add(q.id);
        if (isAnswerableQuestion(q) && q.sensitive === true && q.required) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "a sensitive question cannot be required",
                path: [index, "required"],
            });
        }
        if (requiredWithoutActiveChoice(q)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "a required question needs at least one active option (and a grid an active row and column)",
                path: [index, "required"],
            });
        }
    });
});
/**
 * The questions a NEW answer may target (docs/VOLUNTEER_SIGNUP.md "Changing
 * questions after answers"): retired questions dropped, and on the rest the
 * retired options / grid rows / grid columns dropped — so
 * `buildSurveyAnswersSchema(activeIntakeQuestions(stored))` refuses an answer
 * naming either (the route's half of the rule; SQL refuses retired question
 * keys too). Multi-choice selection bounds are clamped to the active option
 * count, so a required question stays answerable after a retire. Renderers of
 * EXISTING answers keep reading the full stored list (a retired option's label
 * must still resolve). Pure; the input is never mutated.
 */
export function activeIntakeQuestions(questions) {
    return questions.filter(isActive).map((q) => {
        if (q.type === "choice_grid" || q.type === "checkbox_grid") {
            return { ...q, rows: q.rows.filter(isActive), columns: q.columns.filter(isActive) };
        }
        if (q.type === "multi_choice") {
            const options = q.options.filter(isActive);
            const clamp = (n) => (n == null ? n : Math.min(n, options.length));
            return { ...q, options, min_selections: clamp(q.min_selections), max_selections: clamp(q.max_selections) };
        }
        if (q.type === "single_choice" || q.type === "dropdown") {
            return { ...q, options: q.options.filter(isActive) };
        }
        return q;
    });
}
/**
 * The question ids a volunteer has a stored answer to: the keys of their open
 * `intake_answers` and their `sensitive_answers` (`me` on the public page).
 * The input to `creditAnsweredQuestions`.
 */
export function storedIntakeAnswerIds(me) {
    return new Set([...Object.keys(me?.intake_answers ?? {}), ...Object.keys(me?.sensitive_answers ?? {})]);
}
/**
 * The questions a re-ask validates against (the API's intake validator and
 * the public form, one rule): the ACTIVE questions (`activeIntakeQuestions`),
 * with every question the volunteer already answered (`answeredIds`, from
 * `storedIntakeAnswerIds`) made OPTIONAL — the stored answer satisfies
 * `required`, so a re-ask needs only the new answers, and a stored value
 * naming a since-retired option never forces a re-pick. A value that IS sent
 * is still validated in full. Pure.
 */
export function creditAnsweredQuestions(questions, answeredIds) {
    return activeIntakeQuestions(questions).map((q) => isAnswerableQuestion(q) && q.required === true && answeredIds.has(q.id) ? { ...q, required: false } : q);
}
// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
/**
 * Is `value` an IANA zone this runtime knows? `Intl.supportedValuesOf` omits
 * the `UTC` alias (Node 22), which orgs default to, so the check asks
 * `Intl.DateTimeFormat` instead — it accepts exactly the zones (and aliases)
 * the runtime can resolve. Postgres re-checks on write (22023).
 */
export function isValidTimeZone(value) {
    if (!value || value.length > 64)
        return false;
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
const UUID_RE_SRC = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const UUID_RE = new RegExp(`^${UUID_RE_SRC}$`, "i");
/** A well-formed uuid (any case): the `&shifts=` ids of the "already signed up" link, on both ends. */
export const isUuid = (value) => UUID_RE.test(value);
// The shape `generateImagePath` writes (apps/web/src/lib/imageCompression.ts):
// `<org>/images/volunteer_signup_page/<projectId>/<size>-<ts>-<hash>.<ext>`.
const IMAGE_PATH_RE = new RegExp(`^${UUID_RE_SRC}/images/volunteer_signup_page/${UUID_RE_SRC}/[^/]+$`, "i");
const imagePathSchema = z
    .string()
    .max(1024)
    .regex(IMAGE_PATH_RE, "not a volunteer signup page image path")
    // The DB CHECK refuses `..` too; refuse it here so it is a 400, not a constraint error.
    .refine((p) => !p.includes(".."), "not a volunteer signup page image path");
/**
 * The public page's shift-browser layout (`volunteer_signup_pages.layout`,
 * CHECK-pinned to these five; default `list`). Presentation only: claims,
 * waitlists, consent and intake are the same in every layout.
 *  - `list`      day headings, a list of shift rows under each (the default);
 *  - `by_role`   one card per role, its shifts by day inside;
 *  - `day_tabs`  one day's shifts at a time, picked from a row of day chips;
 *  - `time_grid` per day, roles x a time axis (phones fall back to `list`);
 *  - `table`     one compact table of every shift.
 */
export const VOLUNTEER_PAGE_LAYOUTS = ["list", "by_role", "day_tabs", "time_grid", "table"];
export const volunteerPageLayoutSchema = z.enum(VOLUNTEER_PAGE_LAYOUTS);
/**
 * POST / PATCH …/page — presence-keyed (a column moves only when its key is
 * present; `null` clears a nullable one). Creation defaults the timezone to
 * the org's (`COALESCE(org.timezone,'UTC')`) and the title on the route.
 * `status` has its own route; `public_token` is minted by the API.
 */
export const volunteerPageWriteSchema = z
    .object({
    title: surveyText(VOLUNTEER_PAGE_TITLE_MAX, { min: 1 }).optional(),
    welcome_message: optionalSurveyText(VOLUNTEER_WELCOME_MAX, true),
    /** Directions to the venue (Markdown, the escape-first subset; blank → null). */
    getting_there: optionalSurveyText(VOLUNTEER_GETTING_THERE_MAX, true),
    timezone: z.string().refine(isValidTimeZone, "not a known IANA time zone").optional(),
    day_rollover: localTimeSchema.optional(),
    opens_on: calendarDateSchema.nullable().optional(),
    closes_on: calendarDateSchema.nullable().optional(),
    cancel_cutoff_hours: z.number().int().min(0).max(VOLUNTEER_CANCEL_CUTOFF_HOURS_MAX).optional(),
    allow_overlap: z.boolean().optional(),
    /**
     * New waitlist joins (default on). `false` stops new joins only: people
     * already waiting keep their place and still move up; releasing them is
     * its own route (`…/page/release-waitlist`).
     */
    waitlist_enabled: z.boolean().optional(),
    hours_threshold_minutes: z.number().int().min(1).max(VOLUNTEER_HOURS_THRESHOLD_MINUTES_MAX).nullable().optional(),
    require_phone: z.boolean().optional(),
    image_small: imagePathSchema.nullable().optional(),
    image_medium: imagePathSchema.nullable().optional(),
    image_large: imagePathSchema.nullable().optional(),
    image_alt: optionalSurveyText(VOLUNTEER_IMAGE_ALT_MAX),
    intake_questions: intakeQuestionsSchema.optional(),
    sensitive_retention_days: z.number().int().min(1).max(VOLUNTEER_RETENTION_DAYS_MAX).optional(),
    layout: volunteerPageLayoutSchema.optional(),
    verification_mode: z.enum(VOLUNTEER_VERIFICATION_MODES).optional(),
    /**
     * Add confirmed, verified volunteers to People (default off). `true` turns
     * it on through `volunteer_staff_sync_people`, which also links the page's
     * existing confirmed volunteers in the same transaction (`people_synced`
     * in the response); `false` keeps links already made.
     */
    people_sync: z.boolean().optional(),
    /**
     * The planner events the page serves — replaces the set (`[]` = all
     * events). Each must be an event of the page's project.
     */
    event_ids: z.array(uuid).max(VOLUNTEER_PAGE_EVENTS_MAX).refine(uniqueIds, "event_ids must not repeat").optional(),
})
    .strict()
    .refine((v) => !v.opens_on || !v.closes_on || v.opens_on <= v.closes_on, {
    message: "closes_on must be on or after opens_on",
    path: ["closes_on"],
});
/** POST …/page/status — never back to draft. */
export const volunteerPageStatusSchema = z.object({ status: z.enum(["open", "closed"]) }).strict();
// ---------------------------------------------------------------------------
// Consent, volunteers, staff actions
// ---------------------------------------------------------------------------
/** POST …/page/consent-versions — publishes version max+1 (insert-only). */
export const volunteerConsentVersionSchema = z
    .object({
    title: surveyText(VOLUNTEER_CONSENT_TITLE_MAX, { min: 1 }),
    body: surveyText(VOLUNTEER_CONSENT_BODY_MAX, { min: 1, multiline: true }),
})
    .strict();
/** POST …/volunteers — an organiser adds a volunteer by email. */
export const volunteerAddSchema = z
    .object({
    email: emailSchema,
    name: nameSchema,
    phone: phoneSchema,
    locale: localeSchema.optional(),
})
    .strict();
/**
 * PATCH …/volunteers/:id — the organizer's inline edit of the event persona
 * (name, email, phone) in the People grid. Presence-keyed: an absent key is
 * untouched, `phone: null` clears it. The same field rules as
 * `volunteerAddSchema`. Changes the volunteer row only, never People.
 */
export const volunteerPatchSchema = z
    .object({ name: nameSchema.optional(), email: emailSchema.optional(), phone: phoneSchema })
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: "At least one field must be provided" });
/**
 * Assign (or move, with `replace_assignment_id`) a volunteer to a shift.
 * Without `force` a full or overlapping target answers 409
 * `volunteer_needs_confirmation`; with it the row is `over_capacity`.
 */
export const volunteerStaffAssignSchema = z
    .object({
    volunteer_id: uuid,
    shift_id: uuid,
    force: z.boolean().default(false),
    replace_assignment_id: uuid.nullable().optional(),
})
    .strict();
export const VOLUNTEER_STAFF_ACTIONS = ["approve", "decline", "cancel", "confirm_waitlist", "restore_waitlist"];
/** POST …/assignments/:id/status. `force` lets `confirm_waitlist` go over capacity. */
export const volunteerStaffStatusSchema = z
    .object({
    action: z.enum(VOLUNTEER_STAFF_ACTIONS),
    force: z.boolean().default(false),
})
    .strict();
/**
 * POST …/report/model and POST …/report — the printable volunteer report
 * (docs/VOLUNTEER_SIGNUP.md "Report"). The project is the URL; the body is
 * only the document presentation (`lang`, `paperSize`, the same defaults as
 * every other document request). Portrait only, so no `orientation`.
 */
export const volunteerReportRequestSchema = pdfPresentationSchema.strict();
// ---------------------------------------------------------------------------
// Public bodies
// ---------------------------------------------------------------------------
const linkSchema = z.string().regex(VOLUNTEER_LINK_RE, "not a valid link");
/**
 * The address + locale + HONEYPOT of the two email-keyed public bodies
 * (`/link`, `/signup`). `website` is the survey honeypot field: when it is
 * filled the route answers the same 202 and stores nothing
 * (`isSurveyHoneypotFilled`).
 */
const emailRequestFields = {
    email: emailSchema,
    locale: localeSchema.default("en"),
    [SURVEY_HONEYPOT_FIELD]: z.string().max(SURVEY_LONG_TEXT_MAX).nullable().optional(),
};
/** POST /:token/link — request an access link by email. */
export const volunteerLinkRequestSchema = z.object(emailRequestFields).strict();
/** POST /:token/me — the link travels in the body, never in a URL. */
export const volunteerMeSchema = z.object({ link: linkSchema }).strict();
/** The raw answers envelope; the route validates it against the STORED questions. */
const rawAnswersSchema = z
    .record(z.string(), z.unknown())
    .refine((value) => Object.keys(value).length <= VOLUNTEER_INTAKE_QUESTIONS_MAX, "too many answers");
export const volunteerConsentAcceptSchema = z
    .object({
    method: z.enum(["checkbox", "typed_name"]),
    typed_name: surveyText(VOLUNTEER_TYPED_NAME_MAX, { min: 1 }).optional(),
})
    .strict()
    .refine((v) => (v.method === "typed_name") === (v.typed_name !== undefined), {
    message: "typed_name is required for the typed_name method, and only for it",
    path: ["typed_name"],
});
/** POST /:token/claim. */
export const volunteerClaimSchema = z
    .object({
    link: linkSchema,
    profile: z.object({ name: nameSchema, phone: phoneSchema }).strict(),
    answers: rawAnswersSchema.optional(),
    consent: volunteerConsentAcceptSchema.optional(),
    /** The explicit Art. 9 tick: sensitive answers are stored only when true. */
    sensitive_consent: z.boolean().optional(),
    shift_ids: z.array(uuid).min(1).max(VOLUNTEER_CLAIM_SHIFTS_MAX).refine(uniqueIds, "shift_ids must not repeat"),
    join_waitlist: z.boolean().default(false),
})
    .strict();
/**
 * POST /:token/signup — the one-step signup (no link yet): the claim body
 * without `link`, plus the address, locale and honeypot of `/link`. The
 * intake is validated against the STORED questions unconditionally (a first
 * claim by construction). Pages in `link_first` mode refuse it (RV013).
 */
export const volunteerSignupSchema = volunteerClaimSchema.omit({ link: true }).extend(emailRequestFields).strict();
/**
 * POST /:token/me/intake — the re-ask save when the volunteer is not claiming
 * (a question was added since they answered, `me.intake_done === false`).
 * The answers are validated against the ACTIVE stored questions and MERGED
 * into what is stored (a key sent again is overwritten); sensitive answers are
 * stored only with `sensitive_consent: true`. No shift, consent or email.
 */
export const volunteerSaveIntakeSchema = z
    .object({
    link: linkSchema,
    answers: rawAnswersSchema,
    /** The explicit Art. 9 tick: sensitive answers are stored only when true. */
    sensitive_consent: z.boolean().optional(),
})
    .strict();
/**
 * POST /:token/cancel — pinned to the volunteer behind the link. `note` is the
 * volunteer's optional message to the lead (plain text, <= 500; blank → null).
 */
export const volunteerCancelSchema = z
    .object({ link: linkSchema, assignment_id: uuid, note: optionalSurveyText(VOLUNTEER_CANCEL_NOTE_MAX, true) })
    .strict();
//# sourceMappingURL=volunteerSignup.js.map